import { Link, useNavigate } from "react-router-dom";
import { ActionIcon, Badge, Button, Center, Container, Divider, Group, Image, Loader, Menu, Modal, Paper, ScrollArea, SimpleGrid, Space, Stack, Text, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBox, IconChevronLeft, IconCopy, IconDownload, IconDots, IconFile, IconMapPin, IconPencil, IconQrcode, IconTrash, IconBuilding } from "@tabler/icons-react";
import { useState, useMemo, useEffect } from "react";
import moment from "moment";

import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { AssetDto } from "../../models/assets.models";
import { ListWorkOrderDto } from "../../models/workorder.models";
import { useGetAssetQRCodeQuery, useGetAssetChildrenQuery, useResetAssetQRCodeQuery } from "../../api/asset.api";
import { CrudUserDate } from "../Shared/CrudUserDate";
import { ChildAssetsList } from "./ChildAssetsList";
import { AssetWorkOrderList } from "./AssetWorkOrderList";
import classes from "./AssetDetailView.module.css";
import React from "react";

interface AssetDetailViewProps {
	asset?: AssetDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (asset: AssetDto) => void;
	onClose?: () => void;
}

export const AssetDetailView = ({ asset, isLoading, onEdit, onDelete, onClose }: AssetDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditAsset = useAccess(AzureADRoles.Admin);
	const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

	const { data: qrCodeBlob } = useGetAssetQRCodeQuery(asset?.AssetId ?? null, qrCodeModalOpen);
	const { reset } = useResetAssetQRCodeQuery(asset?.AssetId ?? null);
	const { data: childAssets, isPending: isLoadingChildren } = useGetAssetChildrenQuery(asset?.AssetId ?? null);

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const qrCodeUrl = useMemo(() => {
		if (qrCodeBlob) {
			return window.URL.createObjectURL(qrCodeBlob);
		}
		return null;
	}, [qrCodeBlob]);

	useEffect(() => {
		return () => {
			if (qrCodeUrl) {
				window.URL.revokeObjectURL(qrCodeUrl);
				reset();
			}
		};
	}, [qrCodeUrl]);

	const deleteAsset = () => {
		if (!asset) return;
		if (onDelete) onDelete(asset);
	};

	const handleCopyQRCode = async () => {
		if (!qrCodeBlob) return;
		try {
			const item = new ClipboardItem({ 'image/png': qrCodeBlob });
			await navigator.clipboard.write([item]);
		} catch (err) {
			console.error('Failed to copy QR code:', err);
		}
	};

	const handleDownloadQRCode = () => {
		if (!qrCodeUrl || !asset) return;
		try {
			const a = document.createElement('a');
			a.href = qrCodeUrl;
			a.download = `${asset.Name}${asset.AssetTag ? ` (${asset.AssetTag})` : ""} QR Code.png`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} catch (err) {
			console.error('Failed to download QR code:', err);
		}
	};

	if (isLoading) return (
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)" }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	);

	if (!asset) return (
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)" }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconBox size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select an asset to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>
	);

	const imageFiles = asset.Files?.filter(file => file.IsImage) || [];
	const otherFiles = asset.Files?.filter(file => !file.IsImage) || [];

	return (
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 160px)" }}>
				<Stack gap={0} mb="md" justify="center" className={classes["header"]}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{asset.Name}</Title>
								<Text fz="xs" c="dimmed">Created on {moment(asset.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{asset.Deleted == null && <>
									{!isMobile && (
										<Button onClick={onEdit} disabled={!canEditAsset} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									)}
									{isMobile && (
										<Button onClick={onEdit} disabled={!canEditAsset} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									)}
									<Divider orientation="vertical" />
									<Button onClick={() => setQrCodeModalOpen(true)} disabled={!canEditAsset} variant="subtle" radius="xl" px="sm"><IconQrcode stroke={1.5} size={20} /></Button>
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditAsset} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={() => setQrCodeModalOpen(true)} leftSection={<IconQrcode stroke={1.5} />}>View QR Code</Menu.Item>
											<Menu.Divider />
											<Menu.Item onClick={deleteAsset} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</>}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				<Container px="20">
					{asset.Description && (
						<Stack gap={4}>
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
							<Text fz="sm"><span dangerouslySetInnerHTML={{ __html: asset.Description.replaceAll("\n", "<br />") }} /></Text>
							<Space h="md" />
						</Stack>
					)}

					<SimpleGrid cols={isMobile ? 1 : 2} spacing="md" mb="md">
						{asset.AssetTag && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Asset Tag ID</Text>
								<Text fz="sm">{asset.AssetTag}</Text>
							</Stack>
						)}
						{asset.AssetType && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Asset Type</Text>
								<Text fz="sm">{asset.AssetType}</Text>
							</Stack>
						)}
						{asset.Location && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Location</Text>
								<Link to={{ pathname: `/locations/${asset.Location.LocationId}` }} className={classes["link"]}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconMapPin size={14} />}>{asset.Location.Title}</Badge>
								</Link>
							</Stack>
						)}
						{asset.DateOfPurchase && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Date of Purchase</Text>
								<Text fz="sm">{moment.utc(asset.DateOfPurchase).local().format("MMMM D, YYYY")}</Text>
							</Stack>
						)}
						{asset.DateOfInstallation && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Date of Installation</Text>
								<Text fz="sm">{moment.utc(asset.DateOfInstallation).local().format("MMMM D, YYYY")}</Text>
							</Stack>
						)}
						{asset.Make && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Make</Text>
								<Text fz="sm">{asset.Make}</Text>
							</Stack>
						)}
						{asset.Model && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Model</Text>
								<Text fz="sm">{asset.Model}</Text>
							</Stack>
						)}
						{asset.SerialNumber && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Serial Number</Text>
								<Text fz="sm">{asset.SerialNumber}</Text>
							</Stack>
						)}
						{asset.ParentAsset && (
							<Stack gap={4}>
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Parent Asset</Text>
								<Link to={{ pathname: `/assets/${asset.ParentAsset.AssetId}` }} className={classes["link"]}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBox size={14} />}>{asset.ParentAsset.Name}</Badge>
								</Link>
							</Stack>
						)}
					</SimpleGrid>

					{asset.Vendors && asset.Vendors.length > 0 && (
						<Stack gap={4}>
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Vendors</Text>
							<Group gap="xs" wrap="wrap">
								{asset.Vendors.map(vendor => (
									<Link key={vendor.VendorId} to={{ pathname: `/vendors/${vendor.VendorId}` }} className={classes["link"]}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBuilding size={14} />}>{vendor.Name}</Badge>
									</Link>
								))}
							</Group>
						</Stack>
					)}

					<Divider my="md" />

					{imageFiles.length > 0 && (
						<>
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Pictures</Text>
							<Space h="sm" />
							<SimpleGrid cols={isMobile ? 2 : 3} spacing="xs">
								{imageFiles.map(file => (
									<a key={file.FileId} href={file.Url} target="_blank" rel="noreferrer">
										<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: "cover", aspectRatio: "4/3", width: "100%", height: "auto" }} />
									</a>
								))}
							</SimpleGrid>
							<Divider my="md" />
						</>
					)}

					{otherFiles.length > 0 && (
						<>
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Files</Text>
							<Space h="sm" />
							<Stack gap="xs">
								{otherFiles.map(file => (
									<Group key={file.FileId} gap="xs" align="center">
										<ThemeIcon size="sm" radius="xl" variant="light"><IconFile size={14} /></ThemeIcon>
										<a href={file.Url} target="_blank" rel="noreferrer">
											<Text fz="sm">{file.Name}</Text>
										</a>
									</Group>
								))}
							</Stack>
							<Divider my="md" />
						</>
					)}

					<SimpleGrid cols={isMobile ? 1 : 2} mb="md">
						<CrudUserDate label="Created" userId={asset.CreatedBy.UserId} userName={asset.CreatedBy.Name} date={asset.Created} />
						{asset.UpdatedBy && !asset.Deleted && (
							<CrudUserDate label="Updated" userId={asset.UpdatedBy.UserId} userName={asset.UpdatedBy.Name} date={asset.Updated ?? null} />
						)}
						{asset.DeletedBy && (
							<CrudUserDate label="Deleted" userId={asset.DeletedBy.UserId} userName={asset.DeletedBy.Name} date={asset.Deleted ?? null} />
						)}
					</SimpleGrid>
					<ChildAssetsList childAssets={childAssets} isLoading={isLoadingChildren} />
					<AssetWorkOrderList assetId={asset.AssetId} onSelect={handleWorkOrderSelected} />
				</Container>
			</ScrollArea>

			<Modal
				opened={qrCodeModalOpen}
				onClose={() => setQrCodeModalOpen(false)}
				title={<Text fz="xl" fw="bold">Asset QR Code</Text>}
				centered
				size="sm"
				radius="lg"
			>
				<Stack gap="0">
					<Text size="sm" c="dimmed" ta="center" mb="xs">
						This QR code can be scanned to quickly view this asset and its details.
					</Text>
					<Center>
						{qrCodeUrl ? (
							<Image
								src={qrCodeUrl}
								alt={`QR Code for ${asset.Name}`}
								fit="contain"
								w={250}
								h={250}
							/>
						) : (
							<Loader size="md" />
						)}
					</Center>
					<Text size="sm" c="dimmed" ta="center" mt="xs">
						{asset.Name}
					</Text>
					<Group grow mt="sm">
						<Button
							variant="default"
							radius="xl"
							leftSection={<IconCopy size={16} />}
							disabled={!qrCodeBlob}
							onClick={handleCopyQRCode}
						>
							Copy
						</Button>
						<Button
							variant="filled"
							radius="xl"
							leftSection={<IconDownload size={16} />}
							disabled={!qrCodeUrl}
							onClick={handleDownloadQRCode}
						>
							Download
						</Button>
					</Group>
				</Stack>
			</Modal>
		</Paper>
	);
};
